/* @flow */
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { reaction, computed } from 'mobx';
import type { TranslatorProps } from 'react-i18next';
import { translate } from 'react-i18next';
import { Link } from 'react-router-dom';
import RequireForAccess from 'react-access';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import ReactPlaceholder from 'react-placeholder';

import Loading from 'components/Loading/Loading';
import type TypeDashboardStore from 'stores/DashboardStore';
import { createRequestOwnership, updateReassignOwnership } from 'permissions/asset.js';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { isIE } from 'utils/browserDetection';

import WidgetActions from '../../components/WidgetActions';
import InsufficientData from '../../components/InsufficientData/InsufficientData';
import type { CommonWidgetProps } from '../../commonTypes';
import styles from './AssetsByLocation.module.scss';
import NavigationService from 'services/NavigationService';

const STATIC_DATA = [
  {
    location_id: 'static-1',
    assets: '---',
    location_address: '---',
  },
  {
    location_id: 'static-2',
    assets: '---',
    location_address: '---',
  },
];

type AssetsByLocationProps = CommonWidgetProps;

type InjectedProps = {
  dashboardStore: TypeDashboardStore,
} & TranslatorProps;

type State = {
  lat: number,
  lng: number,
  zoom: number,
  id: number | string,
  exists: boolean,
};

@translate()
@inject('dashboardStore')
@observer
export default class AssetsByLocation extends ComponentWithInjectedProps<
  AssetsByLocationProps,
  State,
  InjectedProps
> {
  static LOAD_THUMBNAIL = false;

  state = {
    lat: 0,
    lng: 0,
    zoom: 10,
    id: 0,
    exists: true,
  };

  autoUpdateDisposer: Function;

  @computed get shouldLoadData(): boolean {
    const { size } = this.props;

    return size !== 'thumbnail' || AssetsByLocation.LOAD_THUMBNAIL;
  }

  @computed get widgetData(): any[] | null {
    const { dashboardStore } = this.props;

    if (!this.shouldLoadData) {
      return STATIC_DATA;
    }

    return dashboardStore.locations || null;
  }

  componentDidMount() {
    const { dashboardStore } = this.props;
    this.loadWidgetData(dashboardStore.loadedTradingAccount);

    this.autoUpdateDisposer = reaction(
      () => dashboardStore.loadedTradingAccount,
      ta => {
        this.loadWidgetData(ta);
      }
    );
  }

  componentWillUnmount() {
    this.autoUpdateDisposer();
  }

  async loadWidgetData(tradingAccount: string) {
    const { dashboardStore, size } = this.props;

    if (this.shouldLoadData) {
      await dashboardStore.loadLocations(tradingAccount);
    }
  }

  render() {
    const { dashboardStore, t, size, widgetItem, isFullScreen } = this.props;
    const accountId = dashboardStore.loadedTradingAccount;

    const renderCardContent = () => (
      <React.Fragment>
        <Divider />
        <CardContent className={styles['card-content']}>
          {this.shouldLoadData && dashboardStore.loadingLocations === true ? (
            <Loading />
          ) : this.widgetData && this.widgetData.length > 0 ? (
            <div className={styles['map-wrapper']}>
              <div className={styles['map-list']}>
                <div className={styles['map-list-container']}>
                  {this.widgetData.map((location: any) => (
                    <React.Fragment key={location.location_id}>
                      <Link
                        to={NavigationService.formulateReportViewUri('/assets', {
                          page: 1,
                          filters: [{ key: 'location_id', values: [location.location_id] }],
                        })}
                      >
                        <div className={styles['map-list-item']}>
                          <ReactPlaceholder ready={this.shouldLoadData} rows={3} type="text">
                            <div>
                              <span>{location.assets}</span>
                              {t('general:asset', { count: location.assets })}
                            </div>
                            <div className={styles['map-list-item-address']}>
                              {location.location_address}
                            </div>
                          </ReactPlaceholder>
                        </div>
                      </Link>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <InsufficientData icon={size !== 'thumbnail'} />
          )}
        </CardContent>
      </React.Fragment>
    );

    return (
      <Card className={styles[size]}>
        <CardHeader
          title={t('general:dashboards.assets_by_location')}
          action={<WidgetActions widgetItem={widgetItem} size={size} isFullScreen={isFullScreen} />}
          className={styles['card-header']}
          classes={{ action: styles.cardHeaderAction }}
        />

        {(!isIE || size !== 'thumbnail') && renderCardContent()}

        {size !== 'thumbnail' && (
          <div className={styles['button-container']}>
            <Link
              to={NavigationService.formulateReportViewUri('/assets', {
                page: 1,
                filters: [{ key: 'account', values: [accountId] }],
              })}
            >
              <RequireForAccess permissions={updateReassignOwnership}>
                <Button color="primary" variant="contained">
                  {t('general:actions.assign_asset')}
                </Button>
              </RequireForAccess>
              <RequireForAccess permissions={createRequestOwnership}>
                <Button color="primary" variant="contained">
                  {t('general:actions.request_ownership')}
                </Button>
              </RequireForAccess>
            </Link>
          </div>
        )}
      </Card>
    );
  }
}
